import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaTrash } from 'react-icons/fa';
import dayjs from 'dayjs';
import UserFormModal from './UserFormModal';
import UserViewModal from './UserViewModal';
import Sidebar from './Sidebar';
import {  useNavigate } from 'react-router-dom';
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const API_URL = '/api';

const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Function to map database role values to display values
  const getDisplayRole = (dbRole) => {
    switch (dbRole) {
      case 'Superadmin':
        return 'Super Admin';
      case 'Vendoradmin':
        return 'Vendor Admin';
      case 'Vendoruser':
        return 'Vendor User';
      
      default:
        return dbRole || 'Unknown';
    }
  };

  // Function to get role color for styling
  const getRoleColor = (role) => {
    switch (role) {
      case 'Superadmin':
        return 'bg-red-100 text-red-800';
      case 'Vendoradmin':
        return 'bg-blue-100 text-blue-800';
      case 'Vendoruser':
        return 'bg-green-100 text-green-800';
      
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/user`);
      const data = await res.json();
      setUsers(data);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedUsers = [...users].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      if (key === 'CreatedAt' || key === 'LastLogin') {
        return direction === 'asc'
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return direction === 'asc'
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }
      return 0;
    });
    setUsers(sortedUsers);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="ml-1" />
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowView(true);
  };
  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEdit(true);
    setShowForm(true);
  };
  const handleDeactivate = async (user) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      const response = await fetch(`${API_URL}/user/${user.UserID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, IsActive: 0 }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate user');
      }
      fetchUsers();
    } catch (error) {
      alert(error.message || 'An error occurred while deactivating the user');
    }
  };
  const handleReactivate = async (user) => {
    if (!window.confirm('Are you sure you want to reactivate this user?')) return;
    try {
      const response = await fetch(`${API_URL}/user/${user.UserID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, IsActive: 1 }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate user');
      }
      fetchUsers();
    } catch (error) {
      alert(error.message || 'An error occurred while reactivating the user');
    }
  };
  const handleDelete = async (user) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`${API_URL}/user/${user.UserID}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }
      fetchUsers();
    } catch (error) {
      alert(error.message || 'An error occurred while deleting the user');
    }
  };
  const handleCreate = () => {
    setSelectedUser(null);
    setIsEdit(false);
    setShowForm(true);
  };
  const handleFormSubmit = async (formData, isEditMode) => {
    // The UserFormModal now handles all API calls internally
    // This function just closes the modal and refreshes the user list
    setShowForm(false);
    fetchUsers();
  };

  const handleLogout = () => {
    navigate('/login');
  }
  return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main className="flex-1 p-2 sm:p-8 overflow-y-auto ml-0 sm:ml-16 transition-all duration-300">
      <div className="flex flex-col h-full">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center sm:items-center mb-4 sm:mb-6 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2b6777]">User Master</h1>
          <button
            className="flex items-center justify-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-30 sm:w-auto"
            onClick={handleCreate}
          >
            <FaPlus /> Create New User
          </button>
        </div>
        
        {/* Table Container */}
        <div className="flex-1 overflow-hidden bg-white rounded-2xl shadow-lg">
          <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
            <table className="w-full min-w-[800px] border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('Username')}>
                    <div className="flex items-center justify-center">Username {getSortIcon('Username')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('FullName')}>
                    <div className="flex items-center justify-center">Full Name {getSortIcon('FullName')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('Email')}>
                    <div className="flex items-center justify-center">Email {getSortIcon('Email')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('Phone')}>
                    <div className="flex items-center justify-center">Phone {getSortIcon('Phone')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('Role')}>
                    <div className="flex items-center justify-center">Role {getSortIcon('Role')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('IsActive')}>
                    <div className="flex items-center justify-center">Status {getSortIcon('IsActive')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center border-r border-white text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('CreatedAt')}>
                    <div className="flex items-center justify-center">Created At {getSortIcon('CreatedAt')}</div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">Loading users...</td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">No users found.</td>
                  </tr>
                ) : users.map((user, idx) => (
                  <tr key={user.UserID} className={`transition-all duration-300 group ${user.IsActive ? 'hover:bg-[#eaf6f6] hover:shadow-md' : 'opacity-60'} border-b last:border-b-0 transform hover:scale-[1.01]`}>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                      <button className='cursor-pointer transition-all duration-200 hover:scale-105 text-blue-600 border-b border-blue-600' onClick={() => handleView(user)}>{user.Username}</button>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm whitespace-nowrap font-semibold">{user.FullName}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm break-all max-w-[120px] sm:max-w-[180px]">{user.Email}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm break-all max-w-[80px] sm:max-w-[120px]">{user.Phone}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold shadow-sm ${getRoleColor(user.Role)}`}>
                        {getDisplayRole(user.Role)}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200 ${user.IsActive ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500'}`}>
                        {user.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-700">
  {user.CreatedAt ? dayjs.utc(user.CreatedAt).format("YYYY-MM-DD HH:mm") : "-"}
</td>


                    <td className="px-3 sm:px-6 py-3 sm:py-4 flex gap-1 sm:gap-2 justify-center items-center">
                      <button title="View" className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(user)}><FaEye /></button>
                      <button title="Edit" className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(user)}><FaEdit /></button>
                      {user.IsActive ? (
                        <button title="Deactivate" className="p-1 sm:p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110" onClick={() => handleDeactivate(user)}><FaBan /></button>
                      ) : (
                        <button title="Reactivate" className="p-1 sm:p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110" onClick={() => handleReactivate(user)}><FaCheck /></button>
                      )}
                      <button title="Delete" className="p-1 sm:p-2 rounded-full hover:bg-red-200 text-red-700 transition-all duration-200 hover:scale-110" onClick={() => handleDelete(user)}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Mobile Card View (Hidden on Desktop) */}
        <div className="sm:hidden mt-4 space-y-4">
          {loading ? (
            <div className="text-center py-10 text-[#AAD7D9] font-semibold">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-[#AAD7D9] font-semibold">No users found.</div>
          ) : users.map((user) => (
            <div key={user.UserID} className="bg-white rounded-xl shadow p-4 border border-gray-200">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-[#2b6777]">{user.FullName}</h3>
                  <p className="text-sm text-gray-600">{user.Username}</p>
                </div>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold shadow-sm ${user.IsActive ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500'}`}>
                  {user.IsActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="truncate">{user.Email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="truncate">{user.Phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Role</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold shadow-sm ${getRoleColor(user.Role)}`}>
                    {getDisplayRole(user.Role)}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
<p>{user.CreatedAt ? dayjs.utc(user.CreatedAt).format('YYYY-MM-DD HH:mm') : '-'}</p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button title="View" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(user)}><FaEye /></button>
                <button title="Edit" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(user)}><FaEdit /></button>
                {user.IsActive ? (
                  <button title="Deactivate" className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110" onClick={() => handleDeactivate(user)}><FaBan /></button>
                ) : (
                  <button title="Reactivate" className="p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110" onClick={() => handleReactivate(user)}><FaCheck /></button>
                )}
                <button title="Delete" className="p-2 rounded-full hover:bg-red-200 text-red-700 transition-all duration-200 hover:scale-110" onClick={() => handleDelete(user)}><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Modals */}
        <UserFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedUser}
          isEdit={isEdit}
          loading={formLoading}
        />
        <UserViewModal
          open={showView}
          onClose={() => setShowView(false)}
          user={selectedUser}
        />
      </div>
    </main>
  </div>
);
};

export default UserMaster; 